/**
 * Room assignment notification sound: deduplication window (ms).
 * Used when both socket and push can fire for the same room.
 */
export const ROOM_SOUND_DEDUP_MS = 3000;

/**
 * @typedef {{ id: string | null, ts: number }} LastPlayedState
 */

/**
 * Decide whether to play the room sound and update dedup state.
 * Mirrors App.js `playRoomSoundOnce` logic (including missing roomId).
 *
 * @param {LastPlayedState} lastPlayed
 * @param {string | null | undefined} roomId
 * @param {number} nowMs
 * @param {number} [dedupMs=ROOM_SOUND_DEDUP_MS]
 * @returns {{ play: boolean, nextLastPlayed: LastPlayedState }}
 */
export function resolveRoomSoundPlay(
  lastPlayed,
  roomId,
  nowMs,
  dedupMs = ROOM_SOUND_DEDUP_MS,
) {
  if (!roomId) {
    return { play: true, nextLastPlayed: lastPlayed };
  }
  if (lastPlayed.id === roomId && nowMs - lastPlayed.ts < dedupMs) {
    return { play: false, nextLastPlayed: lastPlayed };
  }
  return { play: true, nextLastPlayed: { id: roomId, ts: nowMs } };
}

/**
 * Whether the given agent appears in `users` (strings or populated objects).
 *
 * @param {unknown} users
 * @param {string | null | undefined} agentId
 * @returns {boolean}
 */
export function isAgentInRoomUsers(users, agentId) {
  if (!agentId || !Array.isArray(users)) return false;
  const aid = String(agentId);
  return users.some((u) => {
    const id = typeof u === "string" ? u : u?._id;
    return id != null && String(id) === aid;
  });
}

/**
 * Whether a create-room / room-created payload is meant for this agent
 * (`users` membership or assigned_to / assignedTo).
 *
 * @param {object | null | undefined} room
 * @param {string | null | undefined} agentId
 * @returns {boolean}
 */
export function isRoomAssignedToAgent(room, agentId) {
  if (!agentId || !room) return false;
  if (isAgentInRoomUsers(room.users, agentId)) return true;
  const assigned =
    room.assigned_to ?? room.assignedTo ?? room.assignedAgentId ?? null;
  if (assigned == null) return false;
  const id =
    typeof assigned === "object"
      ? assigned._id ?? assigned.id ?? null
      : assigned;
  return id != null && String(id) === String(agentId);
}
